import {
  Component,
  Input,
  OnChanges,
  AfterViewInit,
  OnDestroy,
  OnInit,
  ElementRef,
  ViewChild,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import * as d3 from 'd3';
import { ThemeService, Theme } from '../../../../core/services/theme.service';
import { parseToNativeDate } from '../../../utils/date-utils';
import { Subscription } from 'rxjs';

/**
 * Configuration for each series in the chart
 */
export interface ILineAreaSeries {
  /** Unique identifier for the series */
  key: string;
  /** Display name for the legend */
  name: string;
  /** Data points for the series */
  data: ILineAreaDataPoint[];
  /** Line color (optional - uses theme defaults if not provided) */
  lineColor?: string;
  /** Area fill color (optional - derived from lineColor if not provided) */
  areaColor?: string;
  /** Area fill opacity (0-1, default: 0.3) */
  areaOpacity?: number;
  /** Line stroke width (default: 2) */
  strokeWidth?: number;
  /** Line style - 'solid' | 'dashed' | 'dotted' (default: 'solid') */
  lineStyle?: 'solid' | 'dashed' | 'dotted';
  /** Whether to show data points as circles (default: false) */
  showPoints?: boolean;
  /** Whether to show data points as hollow circles (default: false) */
  hollowPoints?: boolean;
  /** Point radius (default: 4) */
  pointRadius?: number;
  /** Point stroke width (default: 2) */
  pointStrokeWidth?: number;
  /** Whether to render vertical drop lines from points to x-axis (default: false) */
  showDropLines?: boolean;
  /** Drop line color (optional) */
  dropLineColor?: string;
  /** Whether to render as vertical bars instead of area (default: false) */
  renderAsBar?: boolean;
  /** Bar width as percentage of available space (0-1, default: 0.6) */
  barWidth?: number;
  /** Whether to show value labels for this series (default: false) */
  showValues?: boolean;
  /** Bar pattern style (default: 'solid') */
  barPattern?: 'solid' | 'diagonal-stripes';
}

/**
 * Data point structure for each series
 */
export interface ILineAreaDataPoint {
  /** Date string in format 'YYYY-MM-DD' or 'YYYY-MM-DD HH:mm:ss' */
  date: string;
  /** Numeric value for the data point */
  value: number;
  /** Optional label for tooltip */
  label?: string;
  /** Optional additional data for tooltip */
  metadata?: Record<string, any>;
}

/**
 * Chart theme configuration
 */
export interface ILineAreaChartTheme {
  /** Background color */
  backgroundColor?: string;
  /** Grid line color */
  gridColor?: string;
  /** Axis line color */
  axisColor?: string;
  /** Axis label color */
  axisLabelColor?: string;
  /** Tooltip background color */
  tooltipBg?: string;
  /** Tooltip border color */
  tooltipBorder?: string;
  /** Tooltip text color */
  tooltipText?: string;
  /** Hover line color */
  hoverLineColor?: string;
  /** Stroke color for data points (default: white, or transparent in dark mode) */
  pointStroke?: string;
  /** Default colors for series (used when series colors not provided) */
  seriesColors?: string[];
}

/**
 * Margin configuration
 */
export interface IChartMargins {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
}

/**
 * Legend configuration
 */
export interface ILegendConfig {
  /** Whether to show the legend */
  show?: boolean;
  /** Legend position */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Legend item alignment */
  align?: 'start' | 'center' | 'end';
  /** Gap between legend items */
  itemGap?: number;
  /** Marker style for legend items */
  markerStyle?: 'circle' | 'square';
}

/**
 * Axis configuration
 */
export interface IAxisConfig {
  /** Whether to show the axis */
  show?: boolean;
  /** Whether to show axis line */
  showAxisLine?: boolean;
  /** Whether to show grid lines */
  showGrid?: boolean;
  /** Grid line style */
  gridStyle?: 'solid' | 'dashed' | 'dotted';
  /** Whether to show grid lines only at data points (for X-axis) */
  gridAtDataPoints?: boolean;
  /** Whether to show only first and last labels */
  showOnlyFirstLast?: boolean;
  /** Whether to show all labels */
  showAllLabels?: boolean;
  /** Whether to show ticks */
  showTicks?: boolean;
  /** Whether to show labels */
  showLabels?: boolean;
  /** Custom tick format function */
  tickFormat?: (value: any) => string;
  /** Number of ticks (for Y axis) */
  tickCount?: number;
  /** Maximum value for Y axis (optional) */
  yScaleMax?: number;
  /** Font size for labels */
  fontSize?: number;
}

/**
 * Tooltip configuration
 */
export interface ITooltipConfig {
  /** Whether to show tooltips */
  show?: boolean;
  /** Whether to show hover line */
  showHoverLine?: boolean;
  /** Whether to show total of all series values */
  showTotal?: boolean;
  /** Custom label for total row */
  totalLabel?: string;
  /** Value format type */
  valueFormat?: 'number' | 'currency' | 'percent';
  /** Custom value formatter */
  valueFormatter?: (value: number, series: ILineAreaSeries, point: ILineAreaDataPoint) => string;
  /** Custom date formatter */
  dateFormatter?: (date: Date) => string;
  /** Whether to show series label (colored dot and name) - default: true */
  showSeriesLabel?: boolean;
}

/**
 * Curve type for the line
 */
export type CurveType = 'linear' | 'monotoneX' | 'cardinal' | 'catmullRom' | 'basis' | 'step' | 'stepBefore' | 'stepAfter';

@Component({
  selector: 'app-line-area-chart',
  templateUrl: './line-area-chart.component.html',
  styleUrls: ['./line-area-chart.component.scss'],
  providers: [CurrencyPipe],
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    'class': 'w-full h-full block'
  }
})
export class LineAreaChartComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  // ============ DATA INPUTS ============
  /** Series data for the chart */
  @Input() series: ILineAreaSeries[] = [];
  /** Explicit legend items (optional - derived from series if not provided) */
  @Input() legendData: { label: string; color: string }[] = [];

  // ============ CHART TITLE ============
  /** Chart title */
  @Input() title: string = '';
  /** Chart subtitle/description */
  @Input() subtitle: string = '';

  // ============ TEXT STYLE CLASSES (Tailwind) ============
  /** Custom Tailwind classes for title */
  @Input() titleClass: string = '';
  /** Custom Tailwind classes for subtitle */
  @Input() subtitleClass: string = '';
  /** Custom Tailwind classes for header container */
  @Input() headerClass: string = '';
  /** Custom Tailwind classes for legend container */
  @Input() legendClass: string = '';
  /** Custom Tailwind classes for legend labels */
  @Input() legendLabelClass: string = '';

  @Input() xAxisPadding: number = 0;
  @Input() showPoints: boolean = true;
  @Input() maxBarWidth: number | undefined = undefined; // Maximum bar width in pixels
  @Input() onlyShowLastPoint: boolean = false;
  @Input() areaOpacity: number | undefined = undefined;

  // ============ DIMENSIONS ============
  /** Custom margins */
  @Input() margins: IChartMargins = {};
  /** Chart height (in pixels or 'auto') */
  @Input() height: number | 'auto' = 'auto';
  /** Whether to use standardized absolute legend positioning */
  @Input() useStandardizedLegend: boolean = false;
  /** Whether to render the legend inside the SVG using D3 for visual parity with other charts */
  @Input() renderLegendInSvg: boolean = false;
  /** Gap between legend items when rendering in SVG */
  @Input() legendGap: number = 24;

  // ============ APPEARANCE ============
  /** Custom theme configuration */
  @Input() theme: ILineAreaChartTheme = {};
  /** Curve type for lines */
  @Input() curveType: 'linear' | 'monotoneX' | 'step' | 'catmullRom' = 'linear';
  /** Whether to animate the chart */
  @Input() animate: boolean = true;
  /** Animation duration in ms */
  @Input() animationDuration: number = 750;
  /** Whether to use gradient fill for areas */
  @Input() useGradientFill: boolean = true;
  /** Gradient direction (vertical or horizontal) */
  @Input() gradientDirection: 'vertical' | 'horizontal' = 'vertical';
  /** Whether to show bars (for the first series with area) */
  @Input() showBars: boolean = true;
  @Input() markerLines: { date: string; label?: string; lineStyle?: string; color?: string }[] = [];

  // ============ AXES CONFIGURATION ============
  /** X-axis configuration */
  @Input() xAxis: IAxisConfig = { show: true, showGrid: false, showAxisLine: true };
  /** Y-axis configuration */
  @Input() yAxis: IAxisConfig = { show: true, showGrid: true, showAxisLine: false, gridStyle: 'dashed' };
  /** Date unit for X-axis formatting */
  @Input() dateUnit: 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year' = 'day';
  /** Left Y-axis label */
  @Input() leftAxisLabel: string = '';
  /** Right Y-axis label */
  @Input() rightAxisLabel: string = '';
  /** Whether to use dual Y-axes (left for bars, right for lines) */
  @Input() useDualYAxis: boolean = false;
  /** Whether to show value labels on data points */
  @Input() showValueLabels: boolean = false;
  /** Position of value labels relative to data points */
  @Input() valueLabelPosition: 'top' | 'center' | 'bottom' = 'top';
  /** Font size for value labels */
  @Input() valueLabelFontSize: number = 11;
  /** Position of value labels relative to data points */
  @Input() labelPosition: 'top' | 'center' | 'bottom' = 'top';
  /** Whether to expand the month domain by 1 month to provide padding. Default is true for existing behavior. */
  @Input() expandMonthDomain: boolean = true;

  // ============ LEGEND CONFIGURATION ============
  /** Legend configuration */
  @Input() legend: ILegendConfig = { show: true, position: 'bottom', align: 'center' };

  // ============ TOOLTIP CONFIGURATION ============
  /** Tooltip configuration */
  @Input() tooltip: ITooltipConfig = { show: true, showHoverLine: true, valueFormat: 'number' };

  // ============ CARD WRAPPER ============
  /** Whether to show card wrapper */
  @Input() showCard: boolean = true;
  /** Card CSS classes */
  @Input() cardClass: string = '';
  /** Custom card padding */
  @Input() cardPadding: string = '1.5rem';

  @Input() hasRenderedLogic: boolean = true;

  @Input() darkThemeClass: string = 'dark:bg-surface-1';

  // ============ INTERNAL STATE ============
  private actualWidth: number = 0;
  private actualHeight: number = 0;

  @ViewChild('chartContainer', { static: false })
  private chartContainer!: ElementRef;

  private resizeObserver!: ResizeObserver;
  private themeSubscription?: Subscription;
  private currentTheme: Theme = 'light';
  private themeService = inject(ThemeService);
  private isViewInitialized = false;
  private hasRenderedOnce = false;
  private hasUserMovedMouse = false;
  private chartId = 'chart-' + Math.random().toString(36).substring(2, 9);

  constructor(private currencyPipe: CurrencyPipe) { }

  ngOnInit(): void {
    // Initialization logic if needed
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['series'] || changes['theme'] || changes['xAxis'] || changes['yAxis'] || changes['showBars'] || changes['areaOpacity'] || changes['xAxisPadding']) &&
      this.isViewInitialized && this.chartContainer?.nativeElement) {
      this.createChart();
    }
  }

  ngAfterViewInit(): void {
    this.isViewInitialized = true;
    this.currentTheme = this.themeService.getTheme();

    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      if (this.chartContainer?.nativeElement) {
        this.createChart();
      }
    });

    // Use setTimeout to ensure the view is fully rendered
    setTimeout(() => {
      if (this.chartContainer) {
        this.resizeObserver = new ResizeObserver(() => {
          this.createChart();
        });
        this.resizeObserver.observe(this.chartContainer.nativeElement);
        this.createChart();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }

  /**
   * Get theme-aware colors with user overrides
   */
  private getThemeColors(): Required<ILineAreaChartTheme> {
    const isDark = this.currentTheme === 'dark';

    const defaultColors = {
      backgroundColor: isDark ? 'transparent' : 'transparent',
      gridColor: isDark ? '#47474e' : '#e5e7eb',
      axisColor: isDark ? '#47474e' : '#d1d5db',
      axisLabelColor: isDark ? '#a1a1aa' : '#6b7280',
      tooltipBg: isDark ? '#1e1e21' : '#ffffff',
      tooltipBorder: isDark ? '#2e2e33' : '#e5e7eb',
      tooltipText: isDark ? '#f4f4f5' : '#111827',
      hoverLineColor: isDark ? '#85858e' : '#9ca3af',
      seriesColors: isDark
        ? ['#f87171', '#34d399', '#60a5fa', '#fbbf24', '#a78bfa', '#f472b6']
        : ['#ef4444', '#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899'],
    };

    return {
      backgroundColor: this.theme.backgroundColor ?? defaultColors.backgroundColor,
      gridColor: this.theme.gridColor ?? defaultColors.gridColor,
      axisColor: this.theme.axisColor ?? defaultColors.axisColor,
      axisLabelColor: this.theme.axisLabelColor ?? defaultColors.axisLabelColor,
      tooltipBg: this.theme.tooltipBg ?? defaultColors.tooltipBg,
      tooltipBorder: this.theme.tooltipBorder ?? defaultColors.tooltipBorder,
      tooltipText: this.theme.tooltipText ?? defaultColors.tooltipText,
      hoverLineColor: this.theme.hoverLineColor ?? defaultColors.hoverLineColor,
      pointStroke: this.theme.pointStroke ?? (isDark ? '#151517' : '#ffffff'),
      seriesColors: this.theme.seriesColors ?? defaultColors.seriesColors,
    };
  }

  /**
   * Get the appropriate D3 curve generator
   */
  private getCurveGenerator(): d3.CurveFactory {
    const curveMap: Record<CurveType, d3.CurveFactory> = {
      linear: d3.curveLinear,
      monotoneX: d3.curveMonotoneX,
      cardinal: d3.curveCardinal,
      catmullRom: d3.curveCatmullRom,
      basis: d3.curveBasis,
      step: d3.curveStep,
      stepBefore: d3.curveStepBefore,
      stepAfter: d3.curveStepAfter,
    };
    return curveMap[this.curveType] || d3.curveMonotoneX;
  }

  /**
   * Parse date string to Date object
   * Handles various formats: 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD', 'YYYY-MM', 'YYYY', and ISO.
   */
  private parseDate(dateStr: string): Date | undefined {
    return parseToNativeDate(dateStr, this.dateUnit);
  }

  /**
   * Format date based on date unit
   */
  private formatDate(date: Date): string {
    const currentYear = new Date().getUTCFullYear();
    const isSameYear = date.getUTCFullYear() === currentYear;

    switch (this.dateUnit) {
      case 'hour':
        return d3.utcFormat('%H:%M')(date);
      case 'day':
        return d3.utcFormat(isSameYear ? '%b %d' : '%b %d %Y')(date);
      case 'week':
        const start = d3.utcWeek.floor(date);
        const end = d3.utcWeek.offset(start, 1);
        const startStr = d3.utcFormat('%b %d')(start);
        const endStr = d3.utcFormat('%b %d')(d3.utcDay.offset(end, -1));
        return `${startStr} - ${endStr}${isSameYear ? '' : ' ' + date.getUTCFullYear()}`;
      case 'month':
        return d3.utcFormat('%b %Y')(date);
      case 'quarter':
        const q = Math.ceil((date.getUTCMonth() + 1) / 3);
        return `Q${q} ${date.getUTCFullYear()}`;
      case 'year':
        return d3.utcFormat('%Y')(date);
      default:
        return d3.utcFormat(isSameYear ? '%b %d' : '%b %d %Y')(date);
    }
  }

  /**
   * Format value based on tooltip configuration
   */
  private formatValue(value: number): string {
    if (this.tooltip.valueFormatter) {
      // Custom formatter gets called at display time
      return value.toLocaleString();
    }

    switch (this.tooltip.valueFormat) {
      case 'currency':
        return this.currencyPipe.transform(value, 'USD', 'symbol', '1.0-2') || `$${value}`;
      case 'percent':
        return `${value.toFixed(1)}%`;
      default:
        return value.toLocaleString('en-US');
    }
  }

  /**
   * Calculate the width needed for Y-axis labels
   */
  private getYAxisWidth(labels: string[]): number {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 50;
    context.font = `${this.yAxis.fontSize || 12}px system-ui, -apple-system, sans-serif`;
    return Math.max(...labels.map((label) => context.measureText(label).width)) + 10;
  }

  /**
   * Create the D3 chart
   */
  private createChart(): void {
    if (!this.series || this.series.length === 0 || !this.chartContainer) return;

    const container = this.chartContainer.nativeElement;
    d3.select(container).selectAll('*').remove();

    const rect = container.getBoundingClientRect();
    this.actualWidth = rect.width || container.clientWidth || 400;
    this.actualHeight = rect.height || container.clientHeight || (typeof this.height === 'number' ? this.height : 300);

    // If container is still zero-sized, the DOM layout hasn't settled yet.
    // Schedule a retry — this happens when the chart is inside a conditionally
    // rendered block (@else) and the parent flex layout resolves asynchronously.
    if (this.actualWidth < 10 || this.actualHeight < 10) {
      setTimeout(() => this.createChart(), 100);
      return;
    }

    const width = this.actualWidth;
    const height = this.actualHeight;
    const marginTop = this.margins.top ?? 20;
    const marginRight = this.margins.right ?? 20;
    const marginBottom = this.margins.bottom ?? (this.xAxis.show ? 40 : 20);
    const marginLeft = this.margins.left ?? 60;

    const colors = this.getThemeColors();
    const curveGenerator = this.getCurveGenerator();

    const isDark = this.currentTheme === 'dark';

    // Render legend in SVG if requested
    if (this.showLegend && this.renderLegendInSvg) {
      // Use dynamic sizing logic or fixed dimensions as needed
      // Temporary SVG for legend rendering if needed, but here we append to main svg later
    }

    // Process all series data
    const processedSeries = this.series.map((s, index) => ({
      ...s,
      lineColor: s.lineColor || colors.seriesColors[index % colors.seriesColors.length],
      areaColor: s.areaColor || s.lineColor || colors.seriesColors[index % colors.seriesColors.length],
      areaOpacity: s.areaOpacity ?? this.areaOpacity ?? 0.3,
      strokeWidth: s.strokeWidth ?? 2,
      lineStyle: s.lineStyle ?? 'solid',
      showPoints: s.renderAsBar ? false : (s.showPoints ?? this.showPoints ?? false),
      hollowPoints: s.hollowPoints ?? false,
      pointRadius: s.pointRadius ?? 4,
      pointStrokeWidth: s.pointStrokeWidth ?? 2,
      data: s.data.map(d => ({
        ...d,
        parsedDate: this.parseDate(d.date),
      }))
      .filter(d => d.parsedDate !== undefined)
      .map(d => d as any)
      .sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime()),
    }));

    // Calculate date range across all series
    const allDates = processedSeries.flatMap(s => s.data.map(d => d.parsedDate));

    // Guard: if no dates could be parsed, nothing to render
    if (allDates.length === 0) return;

    const minDate = d3.min(allDates)!;
    const maxDate = d3.max(allDates)!;

    // Create X scale with pixel-based padding
    // xAxisPadding is in pixels and adjusts the usable range
    const xDomain = (this.dateUnit === 'month' && this.expandMonthDomain)
      ? [d3.utcMonth.floor(minDate), d3.utcMonth.offset(d3.utcMonth.floor(maxDate), 1)]
      : [minDate, maxDate];

    // Calculate bar width if any series render as bars
    let maxBarWidthInSeries = 0;
    const barSeries = processedSeries.filter(s => s.renderAsBar);
    if (barSeries.length > 0) {
      const allDates = processedSeries.flatMap(s => s.data.map(d => d.parsedDate));
      const uniqueDates = Array.from(new Set(allDates.map(d => d.getTime()))).map(t => new Date(t)).sort((a, b) => a.getTime() - b.getTime());
      
      const barWidthRatio = barSeries[0].barWidth ?? 0.5;
      if (uniqueDates.length > 1) {
        const minGap = d3.min(uniqueDates.slice(1).map((d, i) => {
          // Temporarily use a scale without padding to estimate gap
          const tempScale = d3.scaleUtc().domain(xDomain).range([marginLeft, Math.max(marginLeft + 1, width - marginRight)]);
          return tempScale(d) - tempScale(uniqueDates[i]);
        })) || 40;
        const scaleFactor = Math.min(1, uniqueDates.length / 6);
        maxBarWidthInSeries = minGap * barWidthRatio * scaleFactor;
      } else {
        maxBarWidthInSeries = 50;
      }
      
      if (this.maxBarWidth !== undefined) {
        maxBarWidthInSeries = Math.min(maxBarWidthInSeries, this.maxBarWidth);
      }
    }

    const startXPadding = this.xAxisPadding + (maxBarWidthInSeries / 2);

    const xScale = d3.scaleUtc()
      .domain(xDomain)
      .range([marginLeft + startXPadding, width - marginRight - this.xAxisPadding]);

    // Calculate value range for dual Y-axis support
    let yScaleLeft: d3.ScaleLinear<number, number>;
    let yScaleRight: d3.ScaleLinear<number, number>;

    if (this.useDualYAxis) {
      // Separate series into bar series (left) and line series (right)
      const barSeries = processedSeries.filter(s => s.renderAsBar);
      const lineSeries = processedSeries.filter(s => !s.renderAsBar);

      // Calculate max values for each axis with 15% padding
      const maxBarValue = d3.max(barSeries.flatMap(s => s.data.map(d => d.value))) ?? 0;
      const maxLineValue = d3.max(lineSeries.flatMap(s => s.data.map(d => d.value))) ?? 0;

      // Create left Y-scale for bars (with padding to prevent clipping)
      yScaleLeft = d3.scaleLinear()
        .domain([0, maxBarValue * 1.15]) // Add 15% padding
        .range([height - marginBottom, marginTop])
        .nice();

      // Create right Y-scale for lines (with padding to prevent clipping)
      yScaleRight = d3.scaleLinear()
        .domain([0, maxLineValue * 1.15]) // Add 15% padding
        .range([height - marginBottom, marginTop])
        .nice();
    } else {
      // Single Y-axis (existing behavior)
      const allValues = processedSeries.flatMap(s => s.data.map(d => d.value));
      const maxValue = this.yAxis.yScaleMax ?? (d3.max(allValues) ?? 0);
      yScaleLeft = d3.scaleLinear()
        .domain([0, Math.max(maxValue, 1)])  // prevent degenerate [0,0] domain
        .range([height - marginBottom, marginTop]);

      // If yScaleMax is provided, don't use .nice() as it might extend beyond the max
      if (this.yAxis.yScaleMax === undefined) {
        yScaleLeft.nice();
      }
      yScaleRight = yScaleLeft; // Use same scale for both
    }

    // For backward compatibility, keep yScale as yScaleLeft
    const yScale = yScaleLeft;

    // Create SVG
    const svg = d3.select(container)
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('overflow', 'visible'); // Keep visible to show full bars without clipping

    if (this.showLegend && this.renderLegendInSvg) {
      this.appendLegends(svg, width, height, isDark);
    }

    // Add defs for gradients
    const defs = svg.append('defs');

    // Create gradient for each series
    processedSeries.forEach((series, index) => {
      const gradientId = `area-gradient-${this.chartId}-${index}`;
      const gradient = defs.append('linearGradient')
        .attr('id', gradientId)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', this.gradientDirection === 'horizontal' ? '100%' : '0%')
        .attr('y2', this.gradientDirection === 'horizontal' ? '0%' : '100%');

      gradient.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', series.areaColor)
        .attr('stop-opacity', series.areaOpacity);

      gradient.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', series.areaColor)
        .attr('stop-opacity', 0.05);
    });

    // Create diagonal stripe pattern
    const patternId = `diagonal-stripe-${this.chartId}`;
    const pattern = defs.append('pattern')
      .attr('id', patternId)
      .attr('width', 8)
      .attr('height', 8)
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('patternTransform', 'rotate(45)');

    pattern.append('rect')
      .attr('width', 8)
      .attr('height', 8)
      .attr('fill', 'transparent');

    pattern.append('line')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', 8)
      .attr('stroke', 'rgba(255, 255, 255, 0.4)')
      .attr('stroke-width', 4);

    // Draw Y-axis grid lines
    if (this.yAxis.showGrid) {
      const yTicks = yScale.ticks(this.yAxis.tickCount || 6);

      // Filter out the tick that corresponds to the x-axis baseline to prevent overlap/dashing issues
      const gridTicks = (this.xAxis.show && this.xAxis.showAxisLine)
        ? yTicks.filter(d => Math.abs(yScale(d) - (height - marginBottom)) > 0.5)
        : yTicks;

      svg.append('g')
        .attr('class', 'y-grid')
        .selectAll('line')
        .data(gridTicks)
        .join('line')
        .attr('x1', marginLeft)
        .attr('x2', width - marginRight)
        .attr('y1', d => yScale(d))
        .attr('y2', d => yScale(d))
        .attr('stroke', colors.gridColor)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', this.yAxis.gridStyle === 'dashed' ? '4,4' : this.yAxis.gridStyle === 'dotted' ? '2,2' : '0');
    }

    // Draw X-axis grid lines
    if (this.xAxis.showGrid) {
      // If gridAtDataPoints is true, use actual data point dates; otherwise use scale ticks
      let gridDates: Date[];
      if (this.xAxis.gridAtDataPoints) {
        // Get unique dates from all series data points
        const uniqueDates = new Set<number>();
        processedSeries.forEach(series => {
          series.data.forEach(point => {
            uniqueDates.add(point.parsedDate.getTime());
          });
        });
        gridDates = Array.from(uniqueDates)
          .map(time => new Date(time))
          .sort((a, b) => a.getTime() - b.getTime());
      } else {
        gridDates = xScale.ticks();
      }

      svg.append('g')
        .attr('class', 'x-grid')
        .selectAll('line')
        .data(gridDates)
        .join('line')
        .attr('x1', d => xScale(d))
        .attr('x2', d => xScale(d))
        .attr('y1', marginTop)
        .attr('y2', height - marginBottom)
        .attr('stroke', colors.gridColor)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', this.xAxis.gridStyle === 'dashed' ? '4,4' : this.xAxis.gridStyle === 'dotted' ? '2,2' : '0');
    }

    // Create area generator
    const areaGenerator = d3.area<any>()
      .x(d => xScale(d.parsedDate))
      .y0(height - marginBottom)
      .y1(d => yScale(d.value))
      .curve(curveGenerator)
      .defined(d => !isNaN(d.value));

    // Create line generator
    const lineGenerator = d3.line<any>()
      .x(d => xScale(d.parsedDate))
      .y(d => yScale(d.value))
      .curve(curveGenerator)
      .defined(d => !isNaN(d.value));

    // Draw bars and areas (in reverse order so first series is on top) - only if showBars is true
    const areasGroup = svg.append('g').attr('class', 'areas');
    const barsGroup = svg.append('g').attr('class', 'bars');

    if (this.showBars) {
      [...processedSeries].reverse().forEach((series, reverseIndex) => {
        const index = processedSeries.length - 1 - reverseIndex;

        // Check if this series should be rendered as bars
        if (series.renderAsBar) {
          // Calculate bar width - scale down for fewer bars
          const barWidthRatio = series.barWidth ?? 0.5;
          const allDates = processedSeries.flatMap(s => s.data.map(d => d.parsedDate));
          const uniqueDates = Array.from(new Set(allDates.map(d => d.getTime()))).map(t => new Date(t)).sort((a, b) => a.getTime() - b.getTime());

          let barWidth: number;
          if (uniqueDates.length > 1) {
            const minGap = d3.min(uniqueDates.slice(1).map((d, i) => xScale(d) - xScale(uniqueDates[i]))) || 40;
            // Scale down bar width when there are fewer bars
            const scaleFactor = Math.min(1, uniqueDates.length / 6); // Scale based on number of bars
            barWidth = minGap * barWidthRatio * scaleFactor;
          } else {
            // Single bar - use fixed narrow width
            barWidth = 50;
          }

          // Apply max width cap if specified
          if (this.maxBarWidth !== undefined) {
            barWidth = Math.min(barWidth, this.maxBarWidth);
          }

          // Draw bars using LEFT Y-scale
          const barGroup = barsGroup.append('g').attr('class', `bar-group-${series.key}`);

          barGroup.selectAll(`.bar-${series.key}`)
            .data(series.data.filter(d => !isNaN(d.value)))
            .join('rect')
            .attr('class', `bar-${series.key}`)
            .attr('x', d => xScale(d.parsedDate) - barWidth / 2)
            .attr('y', d => yScaleLeft(d.value))
            .attr('width', barWidth)
            .attr('height', d => height - marginBottom - yScaleLeft(d.value))
            .attr('fill', series.areaColor || series.lineColor)
            .attr('opacity', series.areaOpacity ?? 0.8)
            .attr('rx', 4) // Rounded corners
            .attr('ry', 4);

          if (series.barPattern === 'diagonal-stripes') {
            barGroup.selectAll(`.bar-pattern-${series.key}`)
              .data(series.data.filter(d => !isNaN(d.value)))
              .join('rect')
              .attr('class', `bar-pattern-${series.key}`)
              .attr('x', d => xScale(d.parsedDate) - barWidth / 2)
              .attr('y', d => yScaleLeft(d.value))
              .attr('width', barWidth)
              .attr('height', d => height - marginBottom - yScaleLeft(d.value))
              .attr('fill', `url(#diagonal-stripe-${this.chartId})`)
              .attr('rx', 4)
              .attr('ry', 4);
          }

        } else {
          // Render as area using RIGHT Y-scale for lines
          const areaGeneratorRight = d3.area<any>()
            .x(d => xScale(d.parsedDate))
            .y0(height - marginBottom)
            .y1(d => yScaleRight(d.value))
            .curve(curveGenerator)
            .defined(d => !isNaN(d.value));

          const areaPath = areasGroup.append('path')
            .datum(series.data)
            .attr('fill', this.useGradientFill ? `url(#area-gradient-${this.chartId}-${index})` : series.areaColor)
            .attr('fill-opacity', this.useGradientFill ? 1 : series.areaOpacity)
            .attr('d', areaGeneratorRight);

          // Only animate on first render, not on resize
          if (this.animate && (this.hasRenderedLogic ? !this.hasRenderedOnce : true)) {
            const totalLength = (areaPath.node() as SVGPathElement)?.getTotalLength() || 0;
            areaPath
              .attr('opacity', 0)
              .transition()
              .duration(this.animationDuration)
              .attr('opacity', 1);
          }
        }
      });
    }


    // Draw lines
    const linesGroup = svg.append('g').attr('class', 'lines');
    processedSeries.forEach((series) => {
      // Use appropriate Y-scale based on series type
      const lineYScale = series.renderAsBar ? yScaleLeft : yScaleRight;

      const lineGeneratorForSeries = d3.line<any>()
        .x(d => xScale(d.parsedDate))
        .y(d => lineYScale(d.value))
        .curve(curveGenerator)
        .defined(d => !isNaN(d.value));

      const linePath = linesGroup.append('path')
        .datum(series.data)
        .attr('fill', 'none')
        .attr('stroke', series.lineColor)
        .attr('stroke-width', series.strokeWidth)
        .attr('stroke-linecap', 'round')
        .attr('stroke-linejoin', 'round')
        .attr('d', lineGeneratorForSeries);

      // Apply line style (dashed/dotted)
      const getDashArray = () => {
        if (series.lineStyle === 'dashed') return '5,5';
        if (series.lineStyle === 'dotted') return '2,2';
        return 'none'; // solid
      };

      // Only animate on first render, not on resize
      if (this.animate && (this.hasRenderedLogic ? !this.hasRenderedOnce : true)) {
        const totalLength = (linePath.node() as SVGPathElement)?.getTotalLength() || 0;
        linePath
          .attr('stroke-dasharray', `${totalLength} ${totalLength}`)
          .attr('stroke-dashoffset', totalLength)
          .transition()
          .duration(this.animationDuration)
          .ease(d3.easeLinear)
          .attr('stroke-dashoffset', 0)
          .on('end', function () {
            // After animation, apply the actual line style
            const dashArray = getDashArray();
            d3.select(this).attr('stroke-dasharray', dashArray);
          });
      } else {
        // No animation, apply line style directly
        linePath.attr('stroke-dasharray', getDashArray());
      }
    });

    // Draw drop lines
    const dropLinesGroup = svg.append('g').attr('class', 'drop-lines');
    processedSeries.forEach((series) => {
      if (series.showDropLines) {
        const dropYScale = series.renderAsBar ? yScaleLeft : yScaleRight;
        let dropData = series.data.filter(d => !isNaN(d.value));
        if (this.onlyShowLastPoint && dropData.length > 0) {
          dropData = [dropData[dropData.length - 1]];
        }

        dropLinesGroup.selectAll(`.drop-line-${series.key}`)
          .data(dropData)
          .join('line')
          .attr('class', `drop-line-${series.key}`)
          .attr('x1', d => xScale(d.parsedDate))
          .attr('x2', d => xScale(d.parsedDate))
          .attr('y1', d => Math.min(dropYScale(d.value) + (series.pointRadius || 0) + 2, height - marginBottom))
          .attr('y2', height - marginBottom)
          .attr('stroke', series.dropLineColor || series.areaColor || series.lineColor)
          .attr('stroke-width', series.strokeWidth > 0 ? series.strokeWidth : 2)
          .attr('stroke-dasharray', series.lineStyle === 'dashed' ? '5,5' : series.lineStyle === 'dotted' ? '2,2' : 'none');
      }
    });

    // Draw data points
    const pointsGroup = svg.append('g').attr('class', 'points');
    processedSeries.forEach((series) => {
      if (series.showPoints) {
        // Use appropriate Y-scale based on series type
        const pointYScale = series.renderAsBar ? yScaleLeft : yScaleRight;

        // Filter data if onlyShowLastPoint is true
        let pointData = series.data.filter(d => !isNaN(d.value));
        if (this.onlyShowLastPoint && pointData.length > 0) {
          pointData = [pointData[pointData.length - 1]];
        }

        pointsGroup.selectAll(`.point-${series.key}`)
          .data(pointData)
          .join('circle')
          .attr('class', `point-${series.key}`)
          .attr('cx', d => xScale(d.parsedDate))
          .attr('cy', d => pointYScale(d.value))
          .attr('r', series.pointRadius)
          .attr('fill', series.hollowPoints ? colors.pointStroke : series.lineColor)
          .attr('stroke', series.hollowPoints ? series.lineColor : colors.pointStroke)
          .attr('stroke-width', series.pointStrokeWidth);
      }
    });

    // Draw value labels if enabled - only show if Y-axis is also showing
    if (this.showValueLabels && this.yAxis.show) {
      const labelsGroup = svg.append('g').attr('class', 'value-labels');

      processedSeries.forEach((series) => {
        // Only render labels if this series has showValues set to true
        if (series.showValues !== true) return;

        // Use appropriate Y-scale based on series type
        const labelYScale = (series.renderAsBar === true) ? yScaleLeft : yScaleRight;

        // Filter data if onlyShowLastPoint is true
        let labelData = series.data.filter(d => !isNaN(d.value));
        if (this.onlyShowLastPoint && labelData.length > 0) {
          labelData = [labelData[labelData.length - 1]];
        }

        labelsGroup.selectAll(`.label-${series.key}`)
          .data(labelData)
          .join('text')
          .attr('class', `label-${series.key}`)
          .attr('x', d => xScale(d.parsedDate))
          .attr('y', d => {
            const yPos = labelYScale(d.value);
            // Adjust position based on series type and preference
            if (series.renderAsBar) {
              // For bars, place just above or inside top
              return this.valueLabelPosition === 'top' ? yPos - 5 : yPos + 15;
            } else {
              // For lines, place above point
              return yPos - 10;
            }
          })
          .attr('text-anchor', 'middle')
          .attr('fill', series.renderAsBar ? '#ffffff' : colors.axisLabelColor) // White text for bars (if inside) logic requires more complex check, defaulting to theme color or white if inside
          .style('fill', (d) => {
            if (series.renderAsBar && this.valueLabelPosition !== 'top') return '#ffffff'; // Inside bar
            return series.lineColor; // Match series color
          })
          .attr('font-size', this.valueLabelFontSize || 10)
          .attr('font-weight', '600')
          .text(d => this.formatValue(d.value));
      });
    }

    // Draw vertical marker lines
    if (this.markerLines && this.markerLines.length > 0) {
      const markersGroup = svg.append('g').attr('class', 'marker-lines');

      this.markerLines.forEach(marker => {
        const markerDate = this.parseDate(marker.date);
        if (!markerDate) return;
        const xPos = xScale(markerDate);

        // Only draw if within visible range
        if (xPos >= marginLeft && xPos <= width - marginRight) {
          markersGroup.append('line')
            .attr('x1', xPos)
            .attr('x2', xPos)
            .attr('y1', marginTop)
            .attr('y2', height - marginBottom)
            .attr('stroke', marker.color || (this.currentTheme === 'dark' ? '#ffffff44' : '#00000044'))
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', marker.lineStyle === 'solid' ? 'none' : '4,4');

          if (marker.label) {
            markersGroup.append('text')
              .attr('x', xPos + 5)
              .attr('y', marginTop + 15)
              .attr('fill', marker.color || colors.axisLabelColor)
              .attr('font-size', '10px')
              .attr('font-weight', '500')
              .text(marker.label);
          }
        }
      });
    }

    // Draw X-axis
    if (this.xAxis.show) {
      let xTickValues: Date[];

      if (this.xAxis.showOnlyFirstLast) {
        xTickValues = [minDate, maxDate];
      } else if (this.xAxis.showAllLabels) {
        xTickValues = processedSeries[0]?.data.map(d => d.parsedDate) || [];
      } else {
        switch (this.dateUnit) {
          case 'hour':
            xTickValues = d3.utcHour.range(minDate, d3.utcHour.offset(maxDate, 1));
            break;
          case 'day':
            xTickValues = d3.utcDay.range(minDate, d3.utcDay.offset(maxDate, 1));
            break;
          case 'week':
            xTickValues = d3.utcWeek.range(minDate, d3.utcWeek.offset(maxDate, 1));
            break;
          case 'month':
            xTickValues = d3.utcMonth.range(d3.utcMonth.floor(minDate), d3.utcMonth.offset(maxDate, 1));
            break;
          case 'quarter':
            xTickValues = (d3.utcMonth.every(3) || d3.utcMonth).range(d3.utcMonth.floor(minDate), d3.utcMonth.offset(maxDate, 1));
            break;
          case 'year':
            xTickValues = d3.utcYear.range(d3.utcYear.floor(minDate), d3.utcYear.offset(maxDate, 1));
            break;
          default:
            xTickValues = d3.utcDay.range(minDate, d3.utcDay.offset(maxDate, 1));
        }
      }

      // Limit the number of ticks based on available width (max ~10 ticks for readability)
      // but only if we are not explicitly told to show all labels
      if (!this.xAxis.showAllLabels) {
        const defaultMaxTicks = ['week', 'month'].includes(this.dateUnit) ? 14 : 10;
        const maxTicks = this.xAxis.tickCount || defaultMaxTicks;
        if (xTickValues.length > maxTicks) {
          const step = Math.ceil(xTickValues.length / maxTicks);
          xTickValues = xTickValues.filter((_, i) => i % step === 0);
        }
      }

      const xAxisG = svg.append('g')
        .attr('class', 'x-axis')
        .attr('transform', `translate(0,${height - marginBottom})`);

      if (this.xAxis.showAxisLine) {
        xAxisG.append('line')
          .attr('x1', marginLeft)
          .attr('x2', width - marginRight)
          .attr('stroke', colors.axisColor);
      }

      xAxisG.selectAll('.x-tick')
        .data(xTickValues)
        .join('g')
        .attr('class', 'x-tick')
        .attr('transform', d => `translate(${xScale(d)},0)`)
        .call(g => {
          if (this.xAxis.showTicks) {
            g.append('line')
              .attr('y1', 0)
              .attr('y2', 6)
              .attr('stroke', colors.axisColor);
          }
          if (this.xAxis.showLabels !== false) {
            g.append('text')
              .attr('y', 15)
              .attr('text-anchor', 'middle')
              .attr('fill', colors.axisLabelColor)
              .attr('font-size', this.xAxis.fontSize || 12)
              .attr('font-weight', '500')
              .text(d => this.xAxis.tickFormat ? this.xAxis.tickFormat(d) : this.formatDate(d));
          }
        });
    }

    // Draw Y-axis
    if (this.yAxis.show) {
      const yTicks = yScale.ticks(this.yAxis.tickCount || 6);
      let displayTicks = yTicks;

      if (this.yAxis.showOnlyFirstLast && yTicks.length > 0) {
        displayTicks = [yTicks[0], yTicks[yTicks.length - 1]];
      }

      const yAxisG = svg.append('g')
        .attr('class', 'y-axis')
        .attr('transform', `translate(${marginLeft},0)`);

      if (this.yAxis.showAxisLine) {
        yAxisG.append('line')
          .attr('y1', marginTop)
          .attr('y2', height - marginBottom)
          .attr('stroke', colors.axisColor);
      }

      yAxisG.selectAll('.y-tick')
        .data(displayTicks)
        .join('g')
        .attr('class', 'y-tick')
        .attr('transform', d => `translate(0,${yScale(d)})`)
        .call(g => {
          if (this.yAxis.showLabels !== false) {
            g.append('text')
              .attr('x', -8)
              .attr('dy', '0.32em')
              .attr('text-anchor', 'end')
              .attr('fill', colors.axisLabelColor)
              .attr('font-size', this.yAxis.fontSize || 12)
              .text(d => this.yAxis.tickFormat ? this.yAxis.tickFormat(d) : this.formatValue(d));
          }
        });

      // Add left axis label if provided
      if (this.leftAxisLabel) {
        yAxisG.append('text')
          .attr('transform', 'rotate(-90)')
          .attr('y', -marginLeft + 15)
          .attr('x', -(height - marginTop - marginBottom) / 2 - marginTop)
          .attr('text-anchor', 'middle')
          .attr('fill', colors.axisLabelColor)
          .attr('font-size', (this.yAxis.fontSize || 12) + 1)
          .attr('font-weight', '600')
          .text(this.leftAxisLabel);
      }
    }

    // Draw right Y-axis for dual axis mode
    if (this.yAxis.show && this.useDualYAxis === true) {
      const yTicksRight = yScaleRight.ticks(this.yAxis.tickCount || 6);
      const displayTicksRight = yTicksRight.map(d => +d.toFixed(2));

      const yAxisRightG = svg.append('g')
        .attr('class', 'y-axis-right')
        .attr('transform', `translate(${width - marginRight},0)`);

      if (this.yAxis.showAxisLine) {
        yAxisRightG.append('line')
          .attr('y1', marginTop)
          .attr('y2', height - marginBottom)
          .attr('stroke', colors.axisColor);
      }

      yAxisRightG.selectAll('.y-tick-right')
        .data(displayTicksRight)
        .join('g')
        .attr('class', 'y-tick-right')
        .attr('transform', d => `translate(0,${yScaleRight(d)})`)
        .call(g => {
          g.append('text')
            .attr('x', 8)
            .attr('dy', '0.32em')
            .attr('text-anchor', 'start')
            .attr('fill', colors.axisLabelColor)
            .attr('font-size', this.yAxis.fontSize || 12)
            .text(d => this.yAxis.tickFormat ? this.yAxis.tickFormat(d) : this.formatValue(d));
        });

      // Add right axis label if provided
      if (this.rightAxisLabel) {
        yAxisRightG.append('text')
          .attr('transform', 'rotate(90)') // Rotate clockwise for right axis
          .attr('y', -marginRight + 15)
          .attr('x', (height - marginTop - marginBottom) / 2 + marginTop)
          .attr('text-anchor', 'middle')
          .attr('fill', colors.axisLabelColor)
          .attr('font-size', (this.yAxis.fontSize || 12) + 1)
          .attr('font-weight', '600')
          .text(this.rightAxisLabel);
      }
    }

    // Tooltip and hover interactions
    if (this.tooltip.show) {
      const tooltip = d3.select(container)
        .append('div')
        .attr('class', 'line-area-tooltip')
        .style('position', 'absolute')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .style('background', colors.tooltipBg)
        .style('border', `1px solid ${colors.tooltipBorder}`)
        .style('border-radius', '16px')
        .style('padding', '16px')
        .style('box-shadow', '0 12px 32px rgba(0, 0, 0, 0.25)')
        .style('font-size', '13px')
        .style('font-weight', '500')
        .style('z-index', '1000')
        .style('transition', 'opacity 0.15s ease');

      const hoverGroup = svg.append('g')
        .attr('class', 'hover-group')
        .style('display', 'none');

      if (this.tooltip.showHoverLine) {
        hoverGroup.append('line')
          .attr('class', 'hover-line')
          .attr('y1', marginTop)
          .attr('y2', height - marginBottom)
          .attr('stroke', colors.hoverLineColor)
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,4');
      }

      // Add hover circles for each series
      processedSeries.forEach((series) => {
        hoverGroup.append('circle')
          .attr('class', `hover-circle-${series.key}`)
          .attr('r', 5)
          .attr('fill', series.lineColor)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 2)
          .style('display', 'none');
      });

      // Invisible overlay for mouse events
      svg.append('rect')
        .attr('class', 'overlay')
        .attr('x', marginLeft)
        .attr('y', marginTop)
        .attr('width', width - marginLeft - marginRight)
        .attr('height', height - marginTop - marginBottom)
        .attr('fill', 'transparent')
        .on('mouseenter', () => {
          hoverGroup.style('display', null);
          // Reset the flag when mouse leaves and re-enters
          this.hasUserMovedMouse = false;
        })
        .on('mouseleave', () => {
          hoverGroup.style('display', 'none');
          tooltip.style('opacity', 0);
        })
        .on('mousemove', (event: MouseEvent) => {
          // Mark that user has actively moved their mouse
          this.hasUserMovedMouse = true;

          const [mx] = d3.pointer(event);
          const hoveredDate = xScale.invert(mx);

          // Find closest data point across all series
          let closestDistance = Infinity;
          let closestDate: Date | null = null;

          processedSeries.forEach(series => {
            series.data.forEach(d => {
              const dist = Math.abs(d.parsedDate.getTime() - hoveredDate.getTime());
              if (dist < closestDistance) {
                closestDistance = dist;
                closestDate = d.parsedDate;
              }
            });
          });

          if (!closestDate) {
            tooltip.style('opacity', 0);
            return;
          }

          const dataX = xScale(closestDate);

          // Calculate bar width to check if mouse is actually over a bar
          const barSeries = processedSeries.filter(s => s.renderAsBar);
          if (barSeries.length > 0) {
            // Calculate bar width using same logic as bar rendering
            const barWidthRatio = barSeries[0].barWidth ?? 0.5;
            const allDates = processedSeries.flatMap(s => s.data.map(d => d.parsedDate));
            const uniqueDates = Array.from(new Set(allDates.map(d => d.getTime()))).map(t => new Date(t)).sort((a, b) => a.getTime() - b.getTime());

            let barWidth: number;
            if (uniqueDates.length > 1) {
              const minGap = d3.min(uniqueDates.slice(1).map((d, i) => xScale(d) - xScale(uniqueDates[i]))) || 40;
              const scaleFactor = Math.min(1, uniqueDates.length / 6);
              barWidth = minGap * barWidthRatio * scaleFactor;
            } else {
              barWidth = 50;
            }

            // Apply max width cap if specified
            if (this.maxBarWidth !== undefined) {
              barWidth = Math.min(barWidth, this.maxBarWidth);
            }

            // Check if mouse X is within the bar's boundaries
            const barLeft = dataX - barWidth / 2;
            const barRight = dataX + barWidth / 2;
            const isOverBar = mx >= barLeft && mx <= barRight;

            // Only show tooltip if hovering over actual bar
            if (!isOverBar || !this.hasUserMovedMouse) {
              tooltip.style('opacity', 0);
              hoverGroup.style('display', 'none');
              return;
            }
          }

          // Show tooltip and hover elements
          hoverGroup.style('display', null);
          if (this.hasUserMovedMouse) {
            tooltip.style('opacity', 1);
          }

          // Update hover line
          if (this.tooltip.showHoverLine) {
            hoverGroup.select('.hover-line')
              .attr('x1', dataX)
              .attr('x2', dataX);
          }

          // Find a matching data point to get the label (if available)
          let tooltipDateLabel = '';
          for (const series of processedSeries) {
            const point = series.data.find(d => d.parsedDate.getTime() === closestDate!.getTime());
            if (point?.label) {
              tooltipDateLabel = point.label;
              break;
            }
          }

          // Update hover circles and build tooltip content
          let tooltipContent = `
            <div style="font-weight: 600; margin-bottom: 6px; padding-bottom: 6px; border-bottom: 1px solid ${colors.tooltipBorder}; color: ${colors.tooltipText}">
              ${this.tooltip.dateFormatter ? this.tooltip.dateFormatter(closestDate) : (tooltipDateLabel || this.formatDate(closestDate))}
            </div>
          `;

          let total = 0;

          processedSeries.forEach(series => {
            const point = series.data.find(d => d.parsedDate.getTime() === closestDate!.getTime());
            const hoverCircle = hoverGroup.select(`.hover-circle-${series.key}`);

            if (point && !isNaN(point.value)) {
              total += point.value;

              // Use correct Y-scale based on series type - check explicitly for true
              const hoverYScale = (series.renderAsBar === true) ? yScaleLeft : yScaleRight;

              hoverCircle
                .attr('cx', dataX)
                .attr('cy', hoverYScale(point.value))
                .style('display', null);

              const formattedValue = this.tooltip.valueFormatter
                ? this.tooltip.valueFormatter(point.value, series, point)
                : this.formatValue(point.value);

              // Check if series label should be shown (default: true)
              const showSeriesLabel = this.tooltip.showSeriesLabel !== false;

              if (showSeriesLabel) {
                tooltipContent += `
                  <div style="display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 4px;">
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <div style="width: 10px; height: 10px; border-radius: 50%; background: ${series.lineColor}"></div>
                      <span style="color: ${colors.axisLabelColor}">${series.name}</span>
                    </div>
                    <span style="font-weight: 600; color: ${colors.tooltipText}">${formattedValue}</span>
                  </div>
                `;
              } else {
                // Only show the formatted value without series label
                tooltipContent += `
                  <div style="margin-top: 4px; color: ${colors.tooltipText};">
                    ${formattedValue}
                  </div>
                `;
              }
            } else {
              hoverCircle.style('display', 'none');
            }
          });

          // Add total row if enabled. Default to showing if there's more than one series.
          const shouldShowTotal = this.tooltip.showTotal === true ||
            (this.tooltip.showTotal === undefined && processedSeries.length > 1);
          if (shouldShowTotal) {
            const totalLabel = this.tooltip.totalLabel || 'Total';
            const formattedTotal = this.formatValue(total);

            tooltipContent += `
              <div style="display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 6px; padding-top: 6px; border-top: 1px solid ${colors.tooltipBorder};">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <div style="width: 10px; height: 10px; border-radius: 2px; background: ${colors.tooltipText}; opacity: 0.3;"></div>
                  <span style="color: ${colors.tooltipText}; font-weight: 500;">${totalLabel}</span>
                </div>
                <span style="font-weight: 700; color: ${colors.tooltipText}">${formattedTotal}</span>
              </div>
            `;
          }

          tooltip.html(tooltipContent);

          // Position tooltip
          const [ptrX, ptrY] = d3.pointer(event, container);
          const tooltipNode = tooltip.node() as HTMLElement;
          const tooltipWidth = tooltipNode.getBoundingClientRect().width;
          const tooltipHeight = tooltipNode.getBoundingClientRect().height;

          let tooltipLeft = ptrX + 15;
          if (tooltipLeft + tooltipWidth > width) {
            tooltipLeft = ptrX - 15 - tooltipWidth;
          }

          let tooltipTop = ptrY - tooltipHeight - 15;
          if (tooltipTop < 0) {
            tooltipTop = ptrY + 15;
          }
          
          if (tooltipTop + tooltipHeight > height) {
            tooltipTop = height - tooltipHeight;
          }
          
          if (tooltipTop < 0) {
            tooltipTop = 0;
          }

          tooltip
            .style('left', `${tooltipLeft}px`)
            .style('top', `${tooltipTop}px`);
        });
    }

    // Mark that we've rendered once (disables animation on subsequent renders/resizes)
    this.hasRenderedOnce = true;
  }

  /**
   * Append legends to the SVG for visual parity with CylinderChartComponent
   */
  private appendLegends(svg: d3.Selection<any, any, any, any>, width: number, height: number, isDark: boolean) {
    const items = this.legendItems;
    if (!items || items.length === 0) return;

    const legendGroup = svg.append('g').attr('class', 'legend-container');
    const spacing = this.legendGap;
    const circleRadius = 5;
    const textPadding = 12; // Space between circle and text
    
    let totalLegendsWidth = 0;
    const itemWidths: number[] = [];

    // Temporary group to measure text widths
    const tempG = svg.append('g').attr('opacity', 0);
    
    items.forEach((d, i) => {
      const text = tempG.append('text')
        .style('font-size', '12px')
        .text(d.name);
      
      const node = text.node() as SVGTextElement;
      let textWidth = 0;
      if (node) {
        try {
          textWidth = node.getBBox().width;
        } catch (e) {
          textWidth = d.name.length * 7;
        }
      }
      
      const itemWidth = circleRadius * 2 + textPadding + textWidth;
      itemWidths.push(itemWidth);
      totalLegendsWidth += itemWidth + (i < items.length - 1 ? spacing : 0);
    });

    tempG.remove();

    // Center alignment - matching LineAreaChart's legend.align if needed, 
    // but here we follow CylinderChart's default (center)
    let lx = (width - totalLegendsWidth) / 2;
    if (this.legend.align === 'start') lx = this.margins.left || 60;
    else if (this.legend.align === 'end') lx = width - totalLegendsWidth - (this.margins.right || 20);

    const textColor = isDark ? '#a1a1aa' : '#64748b';

    let currentX = lx;
    items.forEach((d, i) => {
      const g = legendGroup.append('g')
        .attr('transform', `translate(${currentX}, ${height - 12})`);
      
      g.append('circle')
        .attr('cx', circleRadius)
        .attr('cy', 6)
        .attr('r', circleRadius)
        .attr('fill', d.color);

      g.append('text')
        .attr('x', circleRadius * 2 + textPadding)
        .attr('y', 10)
        .text(d.name)
        .style('font-size', '12px')
        .style('fill', textColor)
        .style('font-weight', '500');
      
      currentX += itemWidths[i] + spacing;
    });
  }

  /**
   * Get legend items for template
   */
  get legendItems(): { key: string; name: string; color: string }[] {
    if (this.legendData && this.legendData.length > 0) {
      return this.legendData.map((d, i) => ({
        key: `legend-${i}`,
        name: d.label,
        color: d.color || '#cccccc' // Fallback color
      }));
    }
    const colors = this.getThemeColors();
    return this.series.map((s, index) => ({
      key: s.key,
      name: s.name,
      color: s.lineColor || colors.seriesColors[index % colors.seriesColors.length],
    }));
  }

  /**
   * Check if legend should be shown
   */
  get showLegend(): boolean {
    return this.legend.show !== false && this.series.length > 0;
  }

  /**
   * Check if HTML legend should be shown
   */
  get showHtmlLegend(): boolean {
    return this.showLegend && !this.renderLegendInSvg;
  }
}
